import React, { useEffect, useState } from "react";
import { Drawer, Tag } from "antd";
import {
    CloseOutlined,
    CheckCircleOutlined,
    ArrowRightOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";
import { getFormattedAmount } from "Shared/Utils";
import { Tokens } from "Shared/Tokens";

const EVENT_TYPE_META = {
    buy: {
        label: "BUY",
        bg: Tokens.positiveBg,
        color: Tokens.positive,
        border: Tokens.positiveBorder,
    },
    sell: {
        label: "SELL",
        bg: Tokens.accentSubtle,
        color: Tokens.accent,
        border: Tokens.accentBorder,
    },
    deposit: {
        label: "DEPOSIT",
        bg: Tokens.surface,
        color: Tokens.textSecondary,
        border: Tokens.hairline,
    },
    withdrawal: {
        label: "WITHDRAWAL",
        bg: Tokens.warningBg,
        color: Tokens.warning,
        border: Tokens.warningBorder,
    },
};

const SOURCE_LABELS = {
    spot: "Binance Spot Fill",
    "auto-invest": "Binance Auto-Invest Plan",
    convert: "Binance Convert Swap",
    deposit: "Binance On-Chain / Internal Deposit",
    withdrawal: "Binance Custody Withdrawal",
    manual: "Manual Portfolio Entry",
};

const VALUATION_LABELS = {
    direct_spot_fill: "Direct USDT Spot Order Execution",
    auto_invest_execution: "Recurring Auto-Invest Plan Fill",
    convert_usdt_valuation: "Convert Swap Valued at Same-Day USDT Rate",
    zero_cost_deposit: "External Deposit (Added at $0 Cost Basis)",
    proportional_cost_transfer:
        "Custody Transfer (Proportional Cost Basis Removed, $0 Realized P&L)",
    manual_entry: "User-Verified Manual Portfolio Transaction",
};

const formatDateTime = (ts) => {
    const ms = Number(ts || 0);
    if (!ms || !Number.isFinite(ms)) return "Unknown date";
    try {
        return new Date(ms).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (e) {
        return "Unknown date";
    }
};

const formatQty = (val) => {
    const num = Number(val || 0);
    if (!Number.isFinite(num)) return "0";
    if (Math.abs(num) >= 1000) {
        return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
    }
    if (Math.abs(num) >= 1) {
        return num.toLocaleString("en-US", { maximumFractionDigits: 4 });
    }
    return num.toLocaleString("en-US", { maximumFractionDigits: 6 });
};

const signedPrefix = (val) => (Number(val || 0) >= 0 ? "+" : "-");

const LedgerEventDetailDrawer = ({
    event = null,
    open = false,
    onClose = () => {},
    currencySymbol = "$",
    exchangeRate = 1,
}) => {
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 768 : false
    );

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    if (!event) return null;

    const rate =
        Number.isFinite(Number(exchangeRate)) && Number(exchangeRate) > 0
            ? Number(exchangeRate)
            : 1;
    const isManual = event.origin === "manual" || event.source === "manual";
    const effectiveRate = isManual ? 1 : rate;

    const money = (val) =>
        `${currencySymbol}${getFormattedAmount(
            Math.abs(Number(val || 0) * effectiveRate)
        )}`;

    const signedMoney = (val) =>
        `${signedPrefix(val)}${money(Math.abs(Number(val || 0)))}`;

    const typeMeta = EVENT_TYPE_META[event.type] || EVENT_TYPE_META.buy;
    const sourceBadgeLabel = isManual
        ? `Manual · ${event.portfolioName || "Portfolio"}`
        : SOURCE_LABELS[event.source] || "Binance Spot";
    const valuationLabel =
        VALUATION_LABELS[event.valuationMethod] ||
        (isManual
            ? VALUATION_LABELS.manual_entry
            : VALUATION_LABELS.direct_spot_fill);

    const isPositiveFlow = event.type === "buy" || event.type === "deposit";
    const isSell = event.type === "sell";
    const hasRealizedDelta =
        isSell || Math.abs(Number(event.realizedPlDelta || 0)) > 1e-8;
    const realizedPositive = Number(event.realizedPlDelta || 0) >= 0;

    let headerBadgeText = typeMeta.label;
    if (event.source === "auto-invest") {
        headerBadgeText = "AUTO-INVEST";
    } else if (event.source === "convert") {
        headerBadgeText = `CONVERT ${typeMeta.label}`;
    }

    let heroAmountTitle = "Transfer Value";
    if (isSell) {
        heroAmountTitle = "Sale Proceeds";
    } else if (event.type === "buy") {
        heroAmountTitle = "Capital Invested";
    }

    return (
        <Drawer
            open={open}
            push={false}
            onClose={onClose}
            placement={isMobile ? "bottom" : "right"}
            width={isMobile ? "100%" : 480}
            height={isMobile ? "86%" : "100%"}
            closable
            closeIcon={
                <CloseOutlined style={{ color: Tokens.textSecondary }} />
            }
            maskClosable
            maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.72)" }}
            title={
                <div
                    data-testid="ledger-event-drawer-header"
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                        color: Tokens.textPrimary,
                    }}
                >
                    <span style={{ fontWeight: 600, fontSize: "16px" }}>
                        {event.coin}
                    </span>
                    <Tag
                        style={{
                            margin: 0,
                            backgroundColor: typeMeta.bg,
                            color: typeMeta.color,
                            border: `1px solid ${typeMeta.border}`,
                            borderRadius: Tokens.radiusTag,
                            fontFamily: Tokens.fontMono,
                            fontSize: "11px",
                            padding: "0 6px",
                        }}
                    >
                        {headerBadgeText}
                    </Tag>
                    <span
                        style={{
                            fontSize: "11px",
                            color: Tokens.textMuted,
                            fontFamily: Tokens.fontMono,
                        }}
                    >
                        {sourceBadgeLabel}
                    </span>
                </div>
            }
            bodyStyle={{
                backgroundColor: Tokens.bg,
                padding: "20px 16px 32px",
            }}
            headerStyle={{
                backgroundColor: Tokens.bg,
                borderBottom: `1px solid ${Tokens.hairline}`,
            }}
        >
            <div
                data-testid="ledger-event-detail-body"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    maxWidth: Tokens.containerMaxWidth,
                    margin: "0 auto",
                    width: "100%",
                }}
            >
                {/* 1. Hero Execution Summary */}
                <div
                    style={{
                        backgroundColor: Tokens.card,
                        border: `1px solid ${Tokens.hairline}`,
                        borderRadius: Tokens.radiusCard,
                        padding: "16px",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: "12px",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    fontSize: "11px",
                                    color: Tokens.textMuted,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                    marginBottom: "4px",
                                }}
                            >
                                {heroAmountTitle}
                            </div>
                            <div
                                data-testid="ledger-event-hero-amount"
                                style={{
                                    fontSize: "24px",
                                    fontWeight: 600,
                                    color: Tokens.textPrimary,
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {money(event.quoteQty || 0)}
                            </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <div
                                style={{
                                    fontSize: "11px",
                                    color: Tokens.textMuted,
                                    marginBottom: "4px",
                                }}
                            >
                                Quantity Change
                            </div>
                            <div
                                data-testid="ledger-event-hero-qty"
                                style={{
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    color: isPositiveFlow
                                        ? Tokens.positive
                                        : Tokens.textPrimary,
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {isPositiveFlow ? "+" : "-"}
                                {formatQty(event.qty)} {event.coin}
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: "12px",
                            paddingTop: "10px",
                            borderTop: `1px solid ${Tokens.hairline}`,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px",
                            fontSize: "12px",
                            color: Tokens.textSecondary,
                            fontFamily: Tokens.fontMono,
                        }}
                    >
                        <span>
                            Exec price:{" "}
                            {event.unitPrice > 0
                                ? money(event.unitPrice)
                                : "N/A (Zero-cost transfer)"}
                        </span>
                        <span>{formatDateTime(event.time)}</span>
                    </div>
                </div>

                {/* 2. Step-by-Step Ledger Math (Before -> After) */}
                <div
                    data-testid="ledger-event-math-card"
                    style={{
                        backgroundColor: Tokens.card,
                        border: `1px solid ${Tokens.hairline}`,
                        borderRadius: Tokens.radiusCard,
                        padding: "16px",
                    }}
                >
                    <div
                        style={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: Tokens.textPrimary,
                            marginBottom: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <span>Step-by-Step Ledger State (Before → After)</span>
                        <span
                            style={{
                                fontSize: "11px",
                                fontWeight: 400,
                                color: Tokens.textMuted,
                                fontFamily: Tokens.fontMono,
                            }}
                        >
                            {event.coin} Position
                        </span>
                    </div>

                    {[
                        {
                            label: "Holdings Qty",
                            before: `${formatQty(event.beforeQty)} ${
                                event.coin
                            }`,
                            after: `${formatQty(event.afterQty)} ${event.coin}`,
                            delta: `${
                                Number(event.qtyDelta || 0) >= 0 ? "+" : ""
                            }${formatQty(event.qtyDelta)}`,
                        },
                        {
                            label: "Open Cost Basis",
                            before: money(event.beforeCostBasis),
                            after: money(event.afterCostBasis),
                            delta: signedMoney(event.costBasisDelta || 0),
                        },
                        {
                            label: "Avg Buy Price",
                            before: money(event.beforeAvgPrice),
                            after: money(event.afterAvgPrice),
                            delta: null,
                        },
                    ].map((row) => (
                        <div
                            key={row.label}
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "8px 0",
                                borderBottom: `1px solid ${Tokens.hairline}`,
                                fontSize: "12px",
                                gap: "8px",
                            }}
                        >
                            <span style={{ color: Tokens.textSecondary }}>
                                {row.label}
                            </span>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    color: Tokens.textPrimary,
                                    flexWrap: "wrap",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <span style={{ color: Tokens.textMuted }}>
                                    {row.before}
                                </span>
                                <ArrowRightOutlined
                                    style={{
                                        fontSize: "10px",
                                        color: Tokens.textMuted,
                                    }}
                                />
                                <span style={{ fontWeight: 600 }}>
                                    {row.after}
                                </span>
                                {row.delta && (
                                    <span
                                        style={{
                                            fontSize: "11px",
                                            color: Tokens.textSecondary,
                                        }}
                                    >
                                        ({row.delta})
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}

                    {event.runningTotalInvested !== undefined && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                paddingTop: "10px",
                                fontSize: "12px",
                            }}
                        >
                            <span style={{ color: Tokens.textSecondary }}>
                                Running Account Invested
                            </span>
                            <span
                                data-testid="ledger-event-running-invested"
                                style={{
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    fontWeight: 600,
                                    color: Tokens.textPrimary,
                                }}
                            >
                                {event.beforeAccountInvested !== undefined
                                    ? `${money(event.beforeAccountInvested)} → `
                                    : ""}
                                {money(event.runningTotalInvested)}
                            </span>
                        </div>
                    )}
                </div>

                {/* 3. Realized P&L Formula Proof (on Sells) */}
                {hasRealizedDelta && (
                    <div
                        data-testid="ledger-event-realized-proof"
                        style={{
                            backgroundColor: realizedPositive
                                ? Tokens.positiveBg
                                : Tokens.negativeBg,
                            border: `1px solid ${
                                realizedPositive
                                    ? Tokens.positiveBorder
                                    : Tokens.negativeBorder
                            }`,
                            borderRadius: Tokens.radiusCard,
                            padding: "14px 16px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "6px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: Tokens.textPrimary,
                                }}
                            >
                                Realized P&amp;L Booked on This Trade
                            </span>
                            <span
                                style={{
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    fontFamily: Tokens.fontMono,
                                    color: realizedPositive
                                        ? Tokens.positive
                                        : Tokens.negative,
                                }}
                            >
                                {signedMoney(event.realizedPlDelta)}
                            </span>
                        </div>
                        <div
                            style={{
                                fontSize: "11px",
                                color: Tokens.textSecondary,
                                fontFamily: Tokens.fontMono,
                                lineHeight: 1.5,
                            }}
                        >
                            Proceeds{" "}
                            {money(event.effectiveProceeds || event.quoteQty)} −
                            Cost Basis Released{" "}
                            {money(
                                event.costReleased ||
                                    Math.abs(event.costBasisDelta || 0)
                            )}{" "}
                            = {signedMoney(event.realizedPlDelta)}
                        </div>
                        {event.runningCoinRealizedPl !== undefined && (
                            <div
                                style={{
                                    fontSize: "11px",
                                    color: Tokens.textMuted,
                                    fontFamily: Tokens.fontMono,
                                    marginTop: "6px",
                                }}
                            >
                                Cumulative {event.coin} Realized P&amp;L:{" "}
                                {signedMoney(event.runningCoinRealizedPl)}
                            </div>
                        )}
                    </div>
                )}

                {/* 4. Data Provenance & Audit Verification */}
                <div
                    data-testid="ledger-event-provenance-card"
                    style={{
                        backgroundColor: Tokens.card,
                        border: `1px solid ${Tokens.hairline}`,
                        borderRadius: Tokens.radiusCard,
                        padding: "14px 16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "12px",
                            fontWeight: 600,
                            color: Tokens.textPrimary,
                        }}
                    >
                        <SafetyCertificateOutlined
                            style={{ color: Tokens.positive }}
                        />
                        <span>Data Provenance &amp; Audit Proof</span>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "12px",
                            gap: "12px",
                        }}
                    >
                        <span style={{ color: Tokens.textSecondary }}>
                            Source Channel
                        </span>
                        <span
                            style={{
                                color: Tokens.textPrimary,
                                fontFamily: Tokens.fontMono,
                                textAlign: "right",
                            }}
                        >
                            {sourceBadgeLabel}
                        </span>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "12px",
                            gap: "12px",
                        }}
                    >
                        <span style={{ color: Tokens.textSecondary }}>
                            Valuation Rule
                        </span>
                        <span
                            style={{
                                color: Tokens.textPrimary,
                                fontSize: "11px",
                                textAlign: "right",
                            }}
                        >
                            {valuationLabel}
                        </span>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "12px",
                            gap: "12px",
                        }}
                    >
                        <span style={{ color: Tokens.textSecondary }}>
                            Audit Record ID
                        </span>
                        <span
                            style={{
                                color: Tokens.textMuted,
                                fontFamily: Tokens.fontMono,
                                fontSize: "11px",
                            }}
                        >
                            #{event.id}
                        </span>
                    </div>

                    {event.inActiveFifoWindow && (
                        <div
                            style={{
                                marginTop: "4px",
                                padding: "8px 10px",
                                borderRadius: Tokens.radiusControl,
                                backgroundColor: Tokens.positiveBg,
                                border: `1px solid ${Tokens.positiveBorder}`,
                                color: Tokens.positive,
                                fontSize: "11px",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                            }}
                        >
                            <CheckCircleOutlined />
                            <span>
                                Active FIFO Lot: {formatQty(event.activeLotQty)}{" "}
                                {event.coin} from this purchase is still held in
                                your live exchange balance.
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </Drawer>
    );
};

export default LedgerEventDetailDrawer;
